import { Address } from '@guardfinder/address';

import { DateTime } from 'luxon';

import { UniformProvidedByOptions } from '../../../../../shared';
import { EventTypeObject, JOB_TYPES, RepeatCycleTypes } from '../../../../../types';
import { JobRate } from '../Job/VO/job-rate';

/**
 * Represents the date information for a job
 * Used in ReviewDateAndTimeComponent to display start and end dates/times
 */
export class JobDate {
    constructor(
        /**
         * The start date and time of the job
         * Used in ReviewDateAndTimeComponent to display the start date and time
         */
        public readonly startDate: DateTime
    ) {
        if (!this.startDate) {
            throw new Error('Start date is required');
        }
    }

    public getEventEndDate(eventHours: number): DateTime {
        return this.startDate.plus({ hour: eventHours });
    }
}

/**
 * Represents the ongoing job details for a job
 * Used in ReviewDateAndTimeComponent to display repeat cycle information
 */
export class CreateJobOngoingDetails {
    constructor(
        /**
         * The end date and time of the job
         * Used in ReviewDateAndTimeComponent to display the end date and time
         */
        public readonly ongoingJobEndDate: DateTime,
        /**
         * The repeat cycle type (weekly, fortnightly, monthly, custom)
         * Used in ReviewDateAndTimeComponent to determine the repeat cycle text
         */
        public readonly repeatCycle?: RepeatCycleTypes,
        /**
         * The custom repeat cycle type (only used when repeatCycle is custom)
         * Used in ReviewDateAndTimeComponent to display custom repeat cycle type
         */
        public readonly repeatCycleCustomType?: string,
        /**
         * The frequency amount for custom repeat cycles
         * Used in ReviewDateAndTimeComponent to display repeat frequency
         */
        public readonly repeatCycleFrequencyAmount?: number,
        /**
         * The days of the week for the repeat cycle
         * Used in ReviewDateAndTimeComponent to display repeat days
         */
        public readonly repeatDays?: string[],
        /**
         * The end date for the repeat cycle
         * Used in ReviewDateAndTimeComponent to display repeat end date
         */
        public readonly repeatEndDate?: DateTime
    ) {}

    public getEventEndDate(eventHours: number): DateTime {
        return this.ongoingJobEndDate.plus({ hour: eventHours });
    }
}

/**
 * Represents the guard information for a job
 * Used in ReviewDateAndTimeComponent to display guard count and hours per day
 */
export class CreateJobGuards {
    constructor(
        /**
         * The number of guards required for the job
         * Used in ReviewDateAndTimeComponent to display the number of guards
         */
        public readonly guardsCount: number,
        /**
         * The number of hours per day each guard will work
         * Used in ReviewDateAndTimeComponent to display the hours per day
         */
        public readonly hoursGuardPerDay: number
    ) {
        if (!this.guardsCount) {
            throw new Error('Number of guards is required');
        }

        if (!this.hoursGuardPerDay) {
            throw new Error('Hours per day is required');
        }

        if (this.hoursGuardPerDay < 4) {
            throw new Error('Hours per day cannot be less than 4');
        }

        if (this.hoursGuardPerDay > 48) {
            throw new Error('Hours per day cannot exceed 24');
        }
    }
}

/**
 * Represents the primary contact information for a job
 * Used in ReviewPrimaryContactComponent to display contact details
 */
export class CreateJobContact {
    constructor(
        /**
         * The name of the primary contact
         * Used in ReviewPrimaryContactComponent to display the contact name
         */
        public readonly name: string,
        /**
         * The phone number of the primary contact
         * Used in ReviewPrimaryContactComponent to display the contact phone number
         */
        public readonly phone: string,
        /**
         * The email address of the primary contact
         * Used in ReviewPrimaryContactComponent to display the contact email
         */
        public readonly email: string
    ) {
        if (!this.name) {
            throw new Error('Contact name is required');
        }

        if (!this.phone) {
            throw new Error('Contact phone is required');
        }

        if (!this.email) {
            throw new Error('Contact email is required');
        }
    }
}

/**
 * Represents the event information for a job
 * Used in ReviewEventInfoComponent and ReviewJobInfoComponent to display event details
 */
export class CreateJobEventInformation {
    constructor(
        /**
         * The type of event
         * Used in ReviewEventInfoComponent and ReviewJobInfoComponent to display the event type
         */
        public readonly eventType: EventTypeObject,
        /**
         * The address where the event will take place
         * Used in ReviewEventInfoComponent to display the event location
         */
        public readonly eventLocationAddress: Address
    ) {
        if (!this.eventType) {
            throw new Error('Event type is required');
        }

        if (!this.eventLocationAddress) {
            throw new Error('Event location address is required');
        }
    }
}

/**
 * Represents additional information for a job
 * Used in ReviewJobInfoComponent to display additional job details
 */
export class CreateJobAdditionalInformation {
    constructor(
        public readonly jobDescription: string,
        /**
         * Previous security issues at the event location
         * Used in ReviewJobInfoComponent to display previous security issues
         */
        public readonly previousIssues?: string,
        /**
         * Emergency procedures for the event
         * Used in ReviewJobInfoComponent to display emergency procedures
         */
        public readonly emergencyProcedures?: string,
        /**
         * Primary duties for the guards
         * Used in ReviewJobInfoComponent to display guard primary duties
         */
        public readonly guardPrimaryDuties?: string
    ) {
        if (!this.jobDescription) {
            throw new Error('Job description is required');
        }
    }
}

/**
 * Represents uniform information for a job
 * Used in ReviewJobInfoComponent to display uniform details
 */
export class CreateJobUniform {
    constructor(
        /**
         * The type of uniform required
         * To be Used in ReviewJobInfoComponent to display the uniform type
         */
        public readonly uniformType: string,
        /**
         * Who provides the uniform (client or guard)
         * Used in ReviewJobInfoComponent to display who provides the uniform
         */
        public readonly providedBy: UniformProvidedByOptions
    ) {
        if (!this.uniformType) {
            throw new Error('Uniform type is required when uniform is required');
        }

        if (!this.providedBy) {
            throw new Error('Uniform provided by is required when uniform is required');
        }
    }
}

/**
 * Main model class representing a job to be created
 * This class is used throughout the job creation process and in the review screen
 *
 * The fields of this class are used in the following review components:
 * - type: Used in ReviewDateAndTimeComponent to determine if it's an ongoing job
 * - date: Passed to ReviewDateAndTimeComponent to display date and time information
 * - guards: Passed to ReviewDateAndTimeComponent to display guard information
 * - primaryContact: Passed to ReviewPrimaryContactComponent to display contact information
 * - eventInformation: Passed to ReviewEventInfoComponent and ReviewJobInfoComponent to display event details
 * - additionalInformation: Passed to ReviewJobInfoComponent to display additional job details
 * - uniform: Used in ReviewJobInfoComponent to display uniform information
 * - repeatJob: Passed to ReviewDateAndTimeComponent to display ongoing job information
 */
export class Job {
    constructor(
        /**
         * The type of job (one-time or ongoing)
         * Used in ReviewDateAndTimeComponent to determine if it's an ongoing job
         */
        public readonly type: JOB_TYPES,
        /**
         * Date and time information for the job
         * Passed to ReviewDateAndTimeComponent to display date and time information
         */
        public readonly date: JobDate,
        /**
         * Guard information for the job
         * Passed to ReviewDateAndTimeComponent to display guard information
         */
        public readonly guards: CreateJobGuards,
        /**
         * Primary contact information for the job
         * Passed to ReviewPrimaryContactComponent to display contact information
         */
        public readonly primaryContact: CreateJobContact,
        /**
         * Event information for the job
         * Passed to ReviewEventInfoComponent and ReviewJobInfoComponent to display event details
         */
        public readonly eventInformation: CreateJobEventInformation,
        /**
         * Additional information for the job
         * Passed to ReviewJobInfoComponent to display additional job details
         */
        public readonly additionalInformation: CreateJobAdditionalInformation,
        /**
         * Uniform information for the job
         * Used in ReviewJobInfoComponent to display uniform information
         */
        public readonly uniform?: CreateJobUniform,
        /**
         * Ongoing job details for the job
         * Passed to ReviewDateAndTimeComponent to display ongoing job information
         */
        public readonly ongoingJobDetails?: CreateJobOngoingDetails,
        /**
         * The hourly rate for the job
         * Used to calculate the job budget and for job submission
         */
        private jobHourlyRate?: JobRate
    ) {
        if (this.type === JOB_TYPES.ONGOING && !this.ongoingJobDetails?.ongoingJobEndDate) {
            throw new Error('End date is required for ongoing job');
        }
    }

    /**
     * Sets the hourly rate for the job
     * Used in ReviewComponent when submitting the job to set the hourly rate from the JobRate object
     * @param jobRate The JobRate object containing the hourly rate
     */
    setJobHourlyRate(jobRate: JobRate) {
        this.jobHourlyRate = jobRate;
    }

    /**
     * Gets the hourly rate for the job
     * Used when calculating the job budget or displaying the hourly rate
     * @returns The hourly rate as a number
     * @throws Error if the hourly rate has not been set
     */
    getJobHourlyRate(): number {
        if (!this.jobHourlyRate) {
            throw new Error('Job hourly rate is not set');
        }
        return this.jobHourlyRate.rate;
    }

    /**
     * Checks if the hourly rate has been set for the job
     * Used to validate the job before submission
     * @returns True if the hourly rate has been set, false otherwise
     */
    isJobHourlyRateSet(): boolean {
        return !!this.jobHourlyRate;
    }

    /**
     * Calculates the total budget for the job based on the hourly rate
     * Used in ReviewEventBudgetComponent to display the job budget
     * @param hourlyRate The hourly rate to use for the calculation
     * @returns The total budget for the job
     */
    getJobBudget(hourlyRate: number): number {
        return this.guards.hoursGuardPerDay * this.guards.guardsCount * hourlyRate;
    }
}
